/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {DiscoveryCategoryBottomSheet} from '@app/components/bottomsheets/DiscoveryCategoryBottomSheet';
import {DiscoveryGuildCard} from '@app/components/discovery/DiscoveryGuildCard';
import styles from '@app/components/discovery/DiscoveryPage.module.css';
import {Input} from '@app/components/form/Input';
import {Scroller, type ScrollerHandle} from '@app/components/uikit/Scroller';
import {Spinner} from '@app/components/uikit/Spinner';
import foodPatternUrl from '@app/images/i-like-food.svg';
import DiscoveryStore from '@app/stores/DiscoveryStore';
import MobileLayoutStore from '@app/stores/MobileLayoutStore';
import {useLingui} from '@lingui/react/macro';
import {FunnelIcon, MagnifyingGlassIcon} from '@phosphor-icons/react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const PAGE_SIZE = 36;
const GRID_MIN_CARD_WIDTH_PX = 280;
const GRID_GAP_PX = 16;
const ESTIMATED_ROW_HEIGHT_PX = 276;
const SEARCH_DEBOUNCE_MS = 300;
const OVERSCAN_ROWS = 3;

export const DiscoveryPage = observer(function DiscoveryPage() {
	const {t} = useLingui();
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const scrollerRef = useRef<ScrollerHandle>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const isMobile = MobileLayoutStore.enabled;
	const [categorySheetOpen, setCategorySheetOpen] = useState(false);
	const [containerWidth, setContainerWidth] = useState(0);

	const columns = useMemo(
		() => (containerWidth > 0 ? Math.max(1, Math.floor((containerWidth + GRID_GAP_PX) / (GRID_MIN_CARD_WIDTH_PX + GRID_GAP_PX))) : 0),
		[containerWidth],
	);

	const guilds = DiscoveryStore.guilds;
	const rowCount = columns > 0 ? Math.ceil(guilds.length / columns) : 0;
	const hasMore = guilds.length < DiscoveryStore.total;

	const virtualizer = useVirtualizer({
		count: rowCount,
		getScrollElement: () => scrollerRef.current?.getScrollerNode() ?? null,
		estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
		overscan: OVERSCAN_ROWS,
	});

	const loadMore = useCallback(() => {
		if (DiscoveryStore.loading || DiscoveryStore.error || !hasMore) {
			return;
		}
		void DiscoveryStore.search({
			offset: guilds.length,
			limit: PAGE_SIZE,
		});
	}, [guilds.length, hasMore]);

	useEffect(() => {
		const items = virtualizer.getVirtualItems();
		const lastItem = items[items.length - 1];
		if (lastItem && lastItem.index >= rowCount - OVERSCAN_ROWS) {
			loadMore();
		}
	}, [virtualizer.getVirtualItems(), rowCount, loadMore]);

	useEffect(() => {
		const node = containerRef.current;
		if (!node) return;
		const observer = new ResizeObserver(([entry]) => {
			if (entry) {
				setContainerWidth(entry.contentRect.width);
			}
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	const handleSearchChange = useCallback((value: string) => {
		if (searchTimerRef.current) {
			clearTimeout(searchTimerRef.current);
		}
		searchTimerRef.current = setTimeout(() => {
			void DiscoveryStore.search({query: value, offset: 0, limit: PAGE_SIZE});
		}, SEARCH_DEBOUNCE_MS);
	}, []);

	useEffect(() => {
		return () => {
			if (searchTimerRef.current) {
				clearTimeout(searchTimerRef.current);
			}
		};
	}, []);

	const virtualRows = virtualizer.getVirtualItems();

	const gridColumnsStyle = columns > 0 ? `repeat(${columns}, 1fr)` : `repeat(auto-fill, minmax(${GRID_MIN_CARD_WIDTH_PX}px, 1fr))`;

	return (
		<div className={styles.container}>
			<Scroller ref={scrollerRef} showTrack={false}>
				<div className={styles.hero}>
					<div className={styles.heroBackground} aria-hidden>
						<div className={styles.heroPattern} style={{backgroundImage: `url(${foodPatternUrl})`}} />
					</div>
					<div className={styles.heroContent}>
						<div className={styles.heroControls}>
							<Input
								className={styles.searchInput}
								placeholder={t`Search communities...`}
								defaultValue={DiscoveryStore.query}
								onChange={(e) => handleSearchChange(e.target.value)}
								leftIcon={<MagnifyingGlassIcon size={16} weight="bold" />}
							/>
							{isMobile && (
								<button type="button" className={styles.filterButton} onClick={() => setCategorySheetOpen(true)}>
									<FunnelIcon size={20} weight="bold" />
								</button>
							)}
						</div>
					</div>
				</div>

				<div ref={containerRef} className={styles.content}>
					{DiscoveryStore.loading && guilds.length === 0 ? (
						<div className={styles.loadingState}>
							<Spinner />
						</div>
					) : guilds.length > 0 && columns > 0 ? (
						<div
							className={styles.virtualGrid}
							style={{height: virtualizer.getTotalSize()}}
						>
							{virtualRows.map((virtualRow) => {
								const startIndex = virtualRow.index * columns;
								const rowGuilds = guilds.slice(startIndex, startIndex + columns);
								return (
									<div
										key={virtualRow.key}
										ref={virtualizer.measureElement}
										data-index={virtualRow.index}
										className={styles.gridRow}
										style={{
											transform: `translateY(${virtualRow.start}px)`,
											gridTemplateColumns: gridColumnsStyle,
										}}
									>
										{rowGuilds.map((guild) => (
											<DiscoveryGuildCard key={guild.id} guild={guild} />
										))}
									</div>
								);
							})}
						</div>
					) : !DiscoveryStore.loading ? (
						<div className={styles.emptyState}>
							<span className={styles.emptyStateText}>{t`No communities found.`}</span>
						</div>
					) : null}
					{hasMore && DiscoveryStore.loading && (
						<div className={styles.loadingMore}>
							<Spinner />
						</div>
					)}
				</div>
			</Scroller>

			{isMobile && (
				<DiscoveryCategoryBottomSheet isOpen={categorySheetOpen} onClose={() => setCategorySheetOpen(false)} />
			)}
		</div>
	);
});
